
import { Component } from '@theme/component';
import {
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
} from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import {
  DialogCloseEvent,
  DialogOpenEvent,
} from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 */

class PredictiveSearchComponent extends Component {
  requiredRefs = [
    'searchInput',
    'predictiveSearchResults',
    'resetButton',
  ];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  #emptyStateLoaded = false;

  #isKeyboardNavigation = false;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener(
        'keydown',
        this.#handleKeyboardShortcut,
        { signal }
      );

      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        { signal }
      );

      dialog.addEventListener(
        DialogOpenEvent.eventName,
        this.#handleDialogOpen,
        { signal, once: true }
      );

      this.addEventListener(
        'click',
        this.#handleModalClick,
        { signal }
      );
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        if (this.isConnected) {
          this.#loadEmptyState();
        }
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#activeFetch?.abort();
  }

  /**
   * Normalize search text for case-insensitive matching.
   * @param {string} value
   * @returns {string}
   */
  #normalizeText(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Find the product title inside a product result card.
   * If no title element is found, the product is excluded.
   *
   * @param {HTMLElement} card
   * @returns {string}
   */
  #getProductTitle(card) {
    const titleElement = card.querySelector(
      [
        '[data-product-title]',
        '.predictive-search-results__card-title',
        '.resource-card__title',
        '.card__heading',
        '[ref="title"]',
        'h3',
        'h2',
      ].join(', ')
    );

    return titleElement?.textContent?.trim() || '';
  }

  /**
   * Filter product results using product titles only.
   * Every search word must appear in the title.
   *
   * Example:
   * "organic soap" matches "Organic Soap Bar".
   * "organic soap" does not match "Organic Face Cream".
   *
   * @param {string} searchTerm
   */
  #filterProductsByTitle(searchTerm) {
    const results = this.refs.predictiveSearchResults;

    const productWrapper = results.querySelector(
      '.predictive-search-results__wrapper-products'
    );

    if (!productWrapper) return;

    const cards = productWrapper.querySelectorAll(
      '.predictive-search-results__card'
    );

    const normalizedQuery = this.#normalizeText(searchTerm);

    const searchWords = normalizedQuery
      .split(/\s+/)
      .filter(Boolean);

    let visibleProductCount = 0;

    cards.forEach((card) => {
      const title = this.#getProductTitle(card);
      const normalizedTitle = this.#normalizeText(title);

      const matches =
        normalizedTitle.length > 0 &&
        searchWords.every((word) =>
          normalizedTitle.includes(word)
        );

      card.hidden = !matches;

      if (matches) {
        visibleProductCount++;
      }
    });

    productWrapper.hidden = visibleProductCount === 0;
  }

  /**
   * Get all visible, navigable search results.
   */
  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        [
          '.predictive-search-results__wrapper-queries',
          '.predictive-search-results__wrapper-products',
          '.predictive-search-results__list',
        ].join(', ')
      )
    );

    return containers
      .flatMap((container) => {
        if (
          container.classList.contains(
            'predictive-search-results__wrapper-products'
          )
        ) {
          return Array.from(
            container.querySelectorAll(
              '.predictive-search-results__card'
            )
          );
        }

        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__card'
          )
        );
      })
      .filter((item) => {
        if (!(item instanceof HTMLElement)) {
          return false;
        }

        if (item.hidden || item.closest('[hidden]')) {
          return false;
        }

        return true;
      });
  }

  get #currentIndex() {
    return (
      this.#allResultsItems.findIndex(
        (item) => item.getAttribute('aria-selected') === 'true'
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;

    if (!items.length) return;

    let activeItem = null;

    items.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    items.forEach((item, itemIndex) => {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    });

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  /**
   * Maintain search input focus when clicking non-interactive areas.
   */
  #handleModalClick = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button, a, input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * CMD+K shortcut.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (
      !this.#emptyStateLoaded &&
      RecentlyViewed.getProducts().length > 0
    ) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;

    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /**
   * Keyboard navigation.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (
      !this.#allResultsItems.length ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex < totalItems - 1
            ? currentIndex + 1
            : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        if (event.shiftKey) {
          this.#currentIndex =
            currentIndex > 0
              ? currentIndex - 1
              : totalItems - 1;
        } else {
          this.#currentIndex =
            currentIndex < totalItems - 1
              ? currentIndex + 1
              : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();

        this.#currentIndex =
          currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            '[data-single-result-url]'
          );

        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();

          window.location.href =
            singleResultContainer.dataset.singleResultUrl;

          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();

          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchTerm = this.refs.searchInput.value.trim();

          if (!searchTerm) return;

          const searchUrl = new URL(
            Theme.routes.search_url,
            location.origin
          );

          searchUrl.searchParams.set('q', searchTerm);

          window.location.href = searchUrl.toString();
        }

        break;
      }
    }
  };

  /**
   * Clear recently viewed products.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper,
    } = this.refs;

    const elements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (!elements.length) return;

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }

    this.#resetSearch();
  }, 100);

  /**
   * Search as the user types.
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();

    this.#isKeyboardNavigation = false;
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Reset result scroll positions.
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults
        .querySelector('.predictive-search-results__inner')
        ?.scrollTo(0, 0);

      this.querySelector('.predictive-search-form__content')
        ?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch and render predictive search results.
   *
   * Product cards are filtered after rendering, using their titles only.
   *
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(
      Theme.routes.predictive_search_url,
      location.origin
    );

    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const predictiveSearchResults =
      this.refs.predictiveSearchResults;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(
        this.dataset.sectionId,
        false,
        url
      );

      if (abortController.signal.aborted) {
        deferredPromise.reject(
          new Error('Fetch aborted by user')
        );
        return;
      }

      if (!resultsMarkup) {
        deferredPromise.resolve({ totalCount: 0 });
        return;
      }

      // Avoid showing results from an older query.
      if (
        this.#normalizeText(this.refs.searchInput.value) !==
        this.#normalizeText(searchTerm)
      ) {
        deferredPromise.resolve({ totalCount: 0 });
        return;
      }

      morph(predictiveSearchResults, resultsMarkup);

      // Critical: only show products whose titles match every query word.
      this.#filterProductsByTitle(searchTerm);

      this.#resetScrollPositions();

      const resultCount = [
        ...predictiveSearchResults.querySelectorAll(
          '[ref="resultsItems[]"]'
        ),
      ].filter((item) => {
        return (
          !item.hidden &&
          !item.closest('[hidden]')
        );
      }).length;

      deferredPromise.resolve({
        totalCount: resultCount,
      });
    } catch (error) {
      deferredPromise.reject(error);

      if (abortController.signal.aborted) return;

      throw error;
    }
  }

  /**
   * Fetch recently viewed product markup.
   *
   * @returns {Promise<string | null>}
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();

    if (!viewedProducts.length) return null;

    const url = new URL(
      Theme.routes.search_url,
      location.origin
    );

    url.searchParams.set(
      'q',
      viewedProducts
        .map((id) => `id:${id}`)
        .join(' OR ')
    );

    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(
      this.dataset.sectionId,
      false,
      url
    );
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    this.#activeFetch?.abort();

    const abortController = new AbortController();

    this.#activeFetch = abortController;

    return abortController;
  }

  /**
   * Reset results and restore the empty search state.
   */
  #resetSearch = async () => {
    const {
      predictiveSearchResults,
      searchInput,
    } = this.refs;

    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.#isKeyboardNavigation = false;

    searchInput.value = '';

    this.#hideResetButton();

    const abortController = this.#createAbortController();

    const url = new URL(window.location.href);

    url.searchParams.delete('page');

    try {
      const emptySectionMarkup =
        await sectionRenderer.getSectionHTML(
          emptySectionId,
          false,
          url
        );

      if (abortController.signal.aborted) return;

      if (!emptySectionMarkup) return;

      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup) {
        throw new Error(
          'No empty section markup found'
        );
      }

      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup =
          await this.#getRecentlyViewedProductsMarkup();

        if (abortController.signal.aborted) return;

        if (!recentlyViewedMarkup) return;

        const parsedRecentlyViewedMarkup =
          new DOMParser().parseFromString(
            recentlyViewedMarkup,
            'text/html'
          );

        const recentlyViewedProductsHtml =
          parsedRecentlyViewedMarkup.getElementById(
            'predictive-search-products'
          );

        if (!recentlyViewedProductsHtml) return;

        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute(
              'ref',
              'recentlyViewedWrapper'
            );
          }
        }

        const collectionElement =
          parsedEmptySectionMarkup.querySelector(
            '#predictive-search-products'
          );

        if (!collectionElement) return;

        collectionElement.prepend(
          ...recentlyViewedProductsHtml.children
        );
      }

      if (abortController.signal.aborted) return;

      morph(
        predictiveSearchResults,
        parsedEmptySectionMarkup
      );

      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;

      throw error;
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define(
    'predictive-search-component',
    PredictiveSearchComponent
  );
}
